/**
 * Sorts IRF publication entries by the date shown in their citation.
 *
 * Requirement (#1618, follow-up to #1594): within a year, entries with a
 * concrete month/day come first in descending order, bare-year entries come
 * last, and undated entries sort to the very end. Ordering is otherwise
 * descending by year. A stable tie-breaker on the original index preserves the
 * order the middleware delivered for entries with identical dates.
 */

import { InternalIrfPublicationProps } from './fetchData';
import { parseCitationDate } from './parseCitationDate';

export const sortEntriesByCitationDate = (
  entries: InternalIrfPublicationProps[],
): InternalIrfPublicationProps[] =>
  entries
    .map((entry, index) => ({
      entry,
      index,
      date: parseCitationDate(entry.citation_entry),
    }))
    .sort((a, b) => {
      // Undated entries (no parsable year) always sort to the end.
      if (a.date.year === 0 || b.date.year === 0) {
        if (a.date.year === b.date.year) return a.index - b.index;
        return a.date.year === 0 ? 1 : -1;
      }
      // Newest year first.
      if (a.date.year !== b.date.year) return b.date.year - a.date.year;
      // Within a year: concrete month first (month 0 => bare year => last).
      if (a.date.month !== b.date.month) return b.date.month - a.date.month;
      if (a.date.day !== b.date.day) return b.date.day - a.date.day;
      // Stable: keep the middleware's original order for equal dates.
      return a.index - b.index;
    })
    .map(({ entry }) => entry);
